import React from 'react';
import { ChevronDown, UserCog } from 'lucide-react';
import { useAccess } from '../hooks/useAccess';

/**
 * "Acting as" control for the sidebar: the persona the user works under and
 * the Business Cases (data scope) they hold it in. With several eligible
 * personas it is a selector; with one it is a static label. Administrators
 * and users without an assignment see nothing (the Services page explains why).
 */
export const PersonaSwitcher = ({ collapsed = false }) => {
  const { loading, bypass, personas, activePersona, businessCases, setActivePersona } = useAccess();

  if (loading || bypass || personas.length === 0) return null;

  const active = personas.find((persona) => persona.role === activePersona) || personas[0];
  const activeLabel = active.label;
  // A persona is assigned per Business Case; show where the active one applies.
  const scope = Array.isArray(active.businessCases)
    ? active.businessCases.map((code) => businessCases.find((bc) => bc.code === code) || { code, name: code })
    : businessCases;

  if (collapsed) {
    return (
      <div
        className="flex h-11 w-full items-center justify-center rounded-xl bg-white/[0.07]"
        title={`Acting as ${activeLabel}`}
      >
        <UserCog size={15} strokeWidth={2} className="text-[#6BAF65]" aria-hidden="true" />
        <span className="sr-only">{`Acting as ${activeLabel}`}</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white/[0.07] px-3 py-2">
      <label
        htmlFor="persona-switcher"
        className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/55"
      >
        <UserCog size={12} strokeWidth={2} className="text-[#6BAF65]" aria-hidden="true" />
        Acting as
      </label>

      {personas.length > 1 ? (
        <div className="relative mt-1">
          <select
            id="persona-switcher"
            value={activePersona || personas[0].role}
            onChange={(event) => setActivePersona(event.target.value)}
            className="w-full cursor-pointer appearance-none border-0 bg-transparent p-0 pr-5 text-[12px] font-bold text-white outline-none focus-ring"
          >
            {personas.map((persona) => (
              <option key={persona.role} value={persona.role} className="text-black">
                {persona.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-white/55"
            aria-hidden="true"
          />
        </div>
      ) : (
        <p id="persona-switcher" className="mt-1 truncate text-[12px] font-bold text-white">
          {activeLabel}
        </p>
      )}

      {scope.length > 0 && (
        <ul className="m-0 mt-1.5 flex list-none flex-wrap gap-1 p-0" aria-label="Business Cases">
          {scope.map((businessCase) => (
            <li
              key={businessCase.code}
              title={businessCase.name}
              className="rounded-md bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-white/80"
            >
              {businessCase.code}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PersonaSwitcher;
